import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, FileText, AlertTriangle, CheckCircle, Calendar, Building2, DollarSign, RefreshCw, Info, Lock } from "lucide-react";
import { TermLabel, Explained } from "./shared/Help";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useAcaDashboard, useRecompute } from "./shared/dashboardEmployeesHooks";
import {
  FIX_AREA_LABELS, MONTH_LABELS, REPORTING_STATUS_COLORS, REPORTING_STATUS_LABELS, fmtDate, taxYearOptions,
} from "./shared/dashboardEmployeesTypes";

interface ACADashboardProps {
  clientId?: number;
}

export default function ACADashboard({ clientId: propClientId }: ACADashboardProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId;
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const { toast } = useToast();

  const { data: dashboard, isLoading, error } = useAcaDashboard(clientId, taxYear);
  const recompute = useRecompute(clientId, taxYear);

  const handleRecompute = () => {
    recompute.mutate(undefined, {
      onSuccess: (out: any) => toast({ title: "Recalculated", description: `Snapshot v${out?.meta?.version ?? "?"} · ${out?.stats?.blocking ?? 0} blocking, ${out?.stats?.warnings ?? 0} warnings.` }),
      onError: (e: any) => toast({ title: "Recalculation failed", description: e.message, variant: "destructive" }),
    });
  };

  if (!clientId) {
    return <Alert><AlertDescription>No employer selected.</AlertDescription></Alert>;
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => <Card key={i} className="animate-pulse"><CardContent className="h-32" /></Card>)}
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{(error as any)?.message || "Could not load the ACA dashboard."}</AlertDescription>
      </Alert>
    );
  }

  const { ale, deadlines, exceptions, snapshot } = dashboard;
  const aleEstimated = ale.estimated || ale.source === "estimated_current";
  const aleOverride = ale.source === "override";
  const blocking = exceptions.filter((e) => e.severity === "blocking");
  const warnings = exceptions.filter((e) => e.severity !== "blocking");

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <TermLabel term="taxYear"><h2 className="text-xl font-semibold">Tax Year {taxYear}</h2></TermLabel>
            <Explained term="finalize">
              <Badge className={REPORTING_STATUS_COLORS[dashboard.status] || "bg-gray-500"}>
                {dashboard.isLocked && <Lock className="h-3 w-3 mr-1" />}
                {REPORTING_STATUS_LABELS[dashboard.status] || dashboard.status}
              </Badge>
            </Explained>
            <Explained term={dashboard.method === "look-back" ? "lookBack" : "monthlyMeasurement"}>
              <Badge variant="outline">{dashboard.method === "look-back" ? "Look-back measurement" : "Monthly measurement"}</Badge>
            </Explained>
            <Explained term={dashboard.selfInsured ? "selfInsured" : "fullyInsured"}>
              <Badge variant="outline">{dashboard.selfInsured ? "Self-insured" : "Fully insured"}</Badge>
            </Explained>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {taxYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Explained term="recalculate">
              <Button variant="outline" onClick={handleRecompute} disabled={recompute.isPending || dashboard.isLocked}>
                <RefreshCw className={`h-4 w-4 mr-2 ${recompute.isPending ? "animate-spin" : ""}`} />
                Recalculate
              </Button>
            </Explained>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          <TermLabel term="snapshotVersion">Calculation snapshot v{snapshot.version} ({snapshot.status})</TermLabel> · computed {snapshot.computedAt ? new Date(snapshot.computedAt).toLocaleString() : "—"}.
          Every tab, form and count on this module reads from this same employee-by-month calculation.
        </p>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className={ale.isAle ? "border-amber-400" : "border-green-400"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <TermLabel term="ale">ALE Status</TermLabel>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{ale.isAle ? "ALE" : "Not an ALE"}</div>
              <p className="text-xs text-muted-foreground mt-1">
                <TermLabel term="aleAverage">Average {ale.averageFte} FT + FTE</TermLabel>{" "}
                <TermLabel term="aleBasisYear">in {ale.basisYear}</TermLabel>
                {ale.monthsWithData < 12 && ale.source !== "override" ? ` (${ale.monthsWithData} of 12 months with hours data)` : ""}
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {ale.source === "year_end_snapshot" && <Explained term="aleSourceFrozen"><Badge className="bg-blue-600">Frozen year-end snapshot</Badge></Explained>}
                {ale.source === "workforce_snapshot" && <Explained term="aleSourceFrozen"><Badge className="bg-blue-600">Frozen snapshot</Badge></Explained>}
                {ale.source === "prior_year_computed" && !aleOverride && <Explained term="aleSourceLive"><Badge variant="outline">Live recomputation</Badge></Explained>}
                {aleEstimated && !aleOverride && <Explained term="aleEstimated"><Badge variant="destructive">Estimated</Badge></Explained>}
                {aleOverride && <Explained term="aleOverride"><Badge className="bg-purple-600">Override</Badge></Explained>}
                {ale.aggregatedGroup && <Explained term="aggregatedAleGroup"><Badge variant="outline">Aggregated group</Badge></Explained>}
              </div>
              {aleEstimated && !aleOverride && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                  {ale.source === "estimated_current"
                    ? `No ${ale.basisYear} workforce data. This is an estimate from the ${taxYear} workforce, not a final determination.`
                    : `Some ${ale.basisYear} hours were estimated from scheduled hours. Record actual monthly hours to finalize.`}
                </p>
              )}
              {aleOverride && <p className="text-xs text-muted-foreground mt-2">Reason: {ale.overrideReason || "—"}</p>}
              {ale.source === "prior_year_computed" && !aleOverride && (
                <p className="text-xs text-muted-foreground mt-2">No frozen {ale.basisYear} workforce snapshot; computed from live data. Take a year-end snapshot on the History tab to freeze the basis.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /><TermLabel term="population">Population</TermLabel></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboard.population}</div>
              <p className="text-xs text-muted-foreground mt-1">Employees with employment overlapping {taxYear}</p>
              <p className="text-xs mt-2">
                <span className="font-medium">{dashboard.fullTimeEmployees}</span>{" "}
                <TermLabel term="fullTime">ACA full-time for at least one month</TermLabel>
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">130 or more hours of service in a month (30 hours a week).</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /><TermLabel term="formRequired">1095-C Forms Required</TermLabel></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboard.formsRequired}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Employees ACA full-time in any month{dashboard.selfInsured ? ", plus anyone enrolled in self-insured coverage" : ""}
              </p>
              <p className="text-xs mt-2"><TermLabel term="form1094c">+ 1 × Form 1094-C transmittal</TermLabel></p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /><TermLabel term="affordability">Affordability</TermLabel></CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between"><TermLabel term="affordability">Affordable</TermLabel><span className="font-medium text-green-700">{dashboard.affordability.affordable}</span></div>
              <div className="flex justify-between"><TermLabel term="line16Blank">Not affordable</TermLabel><span className="font-medium text-red-700">{dashboard.affordability.notAffordable}</span></div>
              <div className="flex justify-between"><TermLabel term="pendingData">Pending data</TermLabel><span className="font-medium text-amber-700">{dashboard.affordability.pending}</span></div>
              <p className="text-[11px] text-muted-foreground pt-1">Roll-up of 12 monthly determinations per employee.</p>
            </CardContent>
          </Card>
        </div>

        {/* Exception queue */}
        {exceptions.length === 0 ? (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              No compliance exceptions for tax year {taxYear}. All monthly determinations resolved.
            </AlertDescription>
          </Alert>
        ) : (
          <Card className={blocking.length ? "border-red-400" : "border-amber-400"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${blocking.length ? "text-red-600" : "text-amber-600"}`} />
                <TermLabel term="readiness">Action Required</TermLabel>
                <Explained term="exceptionBlocking"><Badge variant="destructive">{blocking.length} blocking</Badge></Explained>
                <Explained term="exceptionWarning"><Badge variant="secondary">{warnings.length} warnings</Badge></Explained>
              </CardTitle>
              <CardDescription>
                Blocking exceptions stop form generation. Missing source data is reported here instead of being replaced with an assumed IRS code.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">Severity</TableHead>
                    <TableHead>Exception</TableHead>
                    <TableHead className="w-[90px] text-right">Months</TableHead>
                    <TableHead className="w-[100px] text-right">Employees</TableHead>
                    <TableHead className="w-[200px]">Fix</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exceptions.map((ex) => (
                    <TableRow key={ex.code}>
                      <TableCell>
                        <Explained term={ex.severity === "blocking" ? "exceptionBlocking" : "exceptionWarning"}>
                          <Badge variant={ex.severity === "blocking" ? "destructive" : ex.severity === "warning" ? "secondary" : "outline"}>
                            {ex.severity}
                          </Badge>
                        </Explained>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{ex.message}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{ex.code}</div>
                      </TableCell>
                      <TableCell className="text-right">{ex.count}</TableCell>
                      <TableCell className="text-right">{ex.employees || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {ex.fix ? (
                          <span>Fix → {FIX_AREA_LABELS[ex.fix.area] || ex.fix.area}{ex.fix.hint ? `: ${ex.fix.hint}` : ""}</span>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ALE monthly detail */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm"><TermLabel term="aleBasisYear">{ale.basisYear} Workforce (ALE basis)</TermLabel></CardTitle>
              <CardDescription>Monthly ACA full-time employees and full-time equivalents used for the {taxYear} ALE determination.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Month</TableHead>
                      {MONTH_LABELS.map((m) => <TableHead key={m} className="text-xs text-center px-1">{m}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-xs font-medium"><TermLabel term="fullTime">Full-time</TermLabel></TableCell>
                      {ale.monthlyFullTime.map((v, i) => <TableCell key={i} className="text-xs text-center px-1">{v}</TableCell>)}
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-xs font-medium"><TermLabel term="fte">FTE</TermLabel></TableCell>
                      {ale.monthlyFte.map((v, i) => <TableCell key={i} className="text-xs text-center px-1">{Number(v).toFixed(1)}</TableCell>)}
                    </TableRow>
                    <TableRow>
                      <TableCell className="text-xs font-medium"><TermLabel term="totalCount1094">All employees</TermLabel></TableCell>
                      {ale.monthlyTotal.map((v, i) => <TableCell key={i} className="text-xs text-center px-1">{v}</TableCell>)}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                <TermLabel term="aleAverage">Average (FT + FTE) ÷ 12, rounded down = {ale.averageFte}.</TermLabel> Source: {ale.source.replace(/_/g, " ")}.
              </p>
            </CardContent>
          </Card>

          {/* Deadlines */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Calendar className="h-4 w-4" />Deadlines for Tax Year {taxYear}</CardTitle>
              <CardDescription>Computed from IRS rules (weekends and federal holidays moved to the next business day).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <TermLabel term="deadlineFurnish">Furnish 1095-C to employees</TermLabel>
                <Badge variant="outline">{fmtDate(deadlines.furnishToEmployees)}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <TermLabel term="deadlinePaper">IRS paper filing (fewer than 10 returns)</TermLabel>
                <Badge variant="outline">{fmtDate(deadlines.paperFiling)}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <TermLabel term="deadlineEfile">IRS electronic filing</TermLabel>
                <Badge variant="outline">{fmtDate(deadlines.electronicFiling)}</Badge>
              </div>
              <div className="pt-2 border-t">
                <div className="text-xs text-muted-foreground mb-1"><TermLabel term="ftCount1094">1094-C monthly full-time count (excludes Limited Non-Assessment Periods)</TermLabel></div>
                <div className="flex flex-wrap gap-1">
                  {dashboard.form1094.monthlyFtCount.map((v, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] font-mono">{MONTH_LABELS[i]} {v}</Badge>
                  ))}
                </div>
              </div>
              <Alert className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Administr generates forms but does not file with the IRS. Record external filing on the History tab.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}
